import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  PortalEntityItem,
  PortalStatusItem,
  PortalDataItem,
  PortalProfileItem,
  CustomModuleItem,
  PortalUserItem,
  PortalUserPermissionItem,
  PortalSettingItem,
} from "@ServerTypes/portal/portal.schema";
import type { CostDatabaseItem } from "@ServerTypes/dsr/dsr.schema";
import type { CurrencySymbolItem } from "@ServerTypes/currency/currency.schema";
import type { CategoryItem } from "@ServerTypes/status/status.schema";

// Re-export item types consumed by other files in the codebase
export type {
  PortalEntityItem,
  PortalStatusItem,
  PortalDataItem,
  PortalProfileItem,
  CustomModuleItem,
  PortalUserItem,
  PortalUserPermissionItem,
  PortalSettingItem,
};

const entityListSlice = createSlice({
  name: "entityList",
  initialState: [] as PortalEntityItem[],
  reducers: {
    setEntityList: (_state: PortalEntityItem[], action: PayloadAction<PortalEntityItem[]>) => action.payload,
  },
});

const statusPortalDataSlice = createSlice({
  name: "statusPortalData",
  initialState: [] as PortalStatusItem[],
  reducers: {
    setPortalStatusData: (_state: PortalStatusItem[], action: PayloadAction<PortalStatusItem[]>) => action.payload,
  },
});

const portalDataSlice = createSlice({
  name: "portalData",
  initialState: {} as PortalDataItem,
  reducers: {
    setPortalData: (_state: PortalDataItem, action: PayloadAction<PortalDataItem | null | undefined>) =>
      action.payload ? action.payload : {},
    mergePortalData: (state: PortalDataItem, action: PayloadAction<Partial<PortalDataItem> | null | undefined>) =>
      action.payload ? { ...state, ...action.payload } : {},
  },
});

const profileDataSlice = createSlice({
  name: "profileData",
  initialState: [] as PortalProfileItem[],
  reducers: {
    setPortalProfiles: (
      _state: PortalProfileItem[],
      action: PayloadAction<PortalProfileItem[] | PortalProfileItem | unknown>,
    ) => (Array.isArray(action.payload) ? (action.payload as PortalProfileItem[]) : []) ?? [],
  },
});

const portalEntityListSlice = createSlice({
  name: "portalEntityList",
  initialState: [] as PortalEntityItem[],
  reducers: {
    setPortalEntityList: (
      _state: PortalEntityItem[],
      action: PayloadAction<PortalEntityItem[] | PortalEntityItem | unknown>,
    ) => (Array.isArray(action.payload) ? (action.payload as PortalEntityItem[]) : []) ?? [],
  },
});

const portalSettingSlice = createSlice({
  name: "portalSetting",
  initialState: [] as PortalSettingItem[],
  reducers: {
    getPortalSetting: (_state: PortalSettingItem[], action: PayloadAction<PortalSettingItem[]>) => action.payload,
  },
});

const customModulesSlice = createSlice({
  name: "customModules",
  initialState: [] as CustomModuleItem[],
  reducers: {
    setCustomModules: (
      _state: CustomModuleItem[],
      action: PayloadAction<CustomModuleItem[] | CustomModuleItem | unknown>,
    ) => (Array.isArray(action.payload) ? (action.payload as CustomModuleItem[]) : []) ?? [],
  },
});

const categoryListSlice = createSlice({
  name: "categoryList",
  initialState: [] as CategoryItem[],
  reducers: {
    setCategoryList: (_state: CategoryItem[], action: PayloadAction<CategoryItem[]>) => action.payload,
  },
});

const portalCategoryListSlice = createSlice({
  name: "portalCategoryList",
  initialState: [] as CategoryItem[],
  reducers: {
    setPortalCategoryList: (_state: CategoryItem[], action: PayloadAction<CategoryItem[] | CategoryItem | unknown>) =>
      (Array.isArray(action.payload) ? (action.payload as CategoryItem[]) : []) ?? [],
  },
});

const userInPortalSlice = createSlice({
  name: "UserInPortal",
  initialState: [] as PortalUserItem[],
  reducers: {
    getPortalUserList: (_state: PortalUserItem[], action: PayloadAction<PortalUserItem[] | undefined | unknown>) =>
      (action.payload as PortalUserItem[]) ?? [],
  },
});

const userCountInPortalSlice = createSlice({
  name: "UserCountInPortal",
  initialState: 0,
  reducers: {
    getPortalUserCount: (_state: number, action: PayloadAction<number | undefined>) => action.payload,
  },
});

const currentUserPermissionsSlice = createSlice({
  name: "currentUserPermissions",
  initialState: [] as PortalUserPermissionItem[],
  reducers: {
    setPortalUserPermission: (_state: PortalUserPermissionItem[], action: PayloadAction<PortalUserPermissionItem[]>) =>
      action.payload,
  },
});

const dsrDataSlice = createSlice({
  name: "dsrData",
  initialState: [] as CostDatabaseItem[],
  reducers: {
    getDsrData: (
      _state: CostDatabaseItem[],
      action: PayloadAction<CostDatabaseItem[] | CostDatabaseItem | null | undefined | unknown>,
    ) => (Array.isArray(action.payload) ? (action.payload as CostDatabaseItem[]) : []) ?? [],
  },
});

const symbolsDataSlice = createSlice({
  name: "symbolsData",
  initialState: [] as CurrencySymbolItem[],
  reducers: {
    getSymbolsData: (_state: CurrencySymbolItem[], action: PayloadAction<CurrencySymbolItem[]>) => action.payload,
  },
});

const renderHeaderSlice = createSlice({
  name: "renderHeader",
  initialState: false,
  reducers: {
    setRenderHeader: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

export const { setEntityList: setEntityListAction } = entityListSlice.actions;
export const { setPortalStatusData: setPortalStatusDataAction } = statusPortalDataSlice.actions;
export const { setPortalData: setPortalDataAction, mergePortalData: mergePortalDataAction } = portalDataSlice.actions;
export const { setPortalProfiles: setPortalProfilesAction } = profileDataSlice.actions;
export const { setPortalEntityList: setPortalEntityListAction } = portalEntityListSlice.actions;
export const { getPortalSetting: getPortalSettingAction } = portalSettingSlice.actions;
export const { setCustomModules: setCustomModulesAction } = customModulesSlice.actions;
export const { setCategoryList: setCategoryListAction } = categoryListSlice.actions;
export const { setPortalCategoryList: setPortalCategoryListAction } = portalCategoryListSlice.actions;
export const { getPortalUserList: getPortalUserListAction } = userInPortalSlice.actions;
export const { getPortalUserCount: getPortalUserCountAction } = userCountInPortalSlice.actions;
export const { setPortalUserPermission: setPortalUserPermissionAction } = currentUserPermissionsSlice.actions;
export const { getDsrData: getDsrDataAction } = dsrDataSlice.actions;
export const { getSymbolsData: getSymbolsDataAction } = symbolsDataSlice.actions;
export const { setRenderHeader } = renderHeaderSlice.actions;

export const setEntityList = entityListSlice.reducer;
export const statusPortalData = statusPortalDataSlice.reducer;
export const portalData = portalDataSlice.reducer;
export const profileData = profileDataSlice.reducer;
export const portalEntityList = portalEntityListSlice.reducer;
export const getPortalSetting = portalSettingSlice.reducer;
export const customModules = customModulesSlice.reducer;
export const setCategoryList = categoryListSlice.reducer;
export const setPortalCategoryList = portalCategoryListSlice.reducer;
export const UserInPortal = userInPortalSlice.reducer;
export const UserCountInPortal = userCountInPortalSlice.reducer;
export const currentUserPermissions = currentUserPermissionsSlice.reducer;
export const getDsrData = dsrDataSlice.reducer;
export const getSymbolsData = symbolsDataSlice.reducer;
export const renderHeader = renderHeaderSlice.reducer;
